import { useLearningManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  toApiLearningMetricFilter,
  toExperienceRatingSeries,
} from "../../../../domain/mappers/metricsMappers";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import { normalizeMetricDateRange } from "../../utils/metricDateRange";
import {
  computeOverallRating,
  extractRatingValues,
} from "../../utils/ratingUtils";
import type { ExperienceFeedbackRating, FeedbackOverallRating } from "../types";

export const FEEDBACK_RATINGS_QUERY_KEY = "feedbackRatings";

interface UseInternalFeedbackRatingsParams {
  startDate: Date;
  endDate: Date;
  contentMetadataIds?: string[];
  countries?: string[];
  enabled?: boolean;
  experienceOptions?: { value: string; label: string }[];
}

interface UseInternalFeedbackRatingsResult {
  data: ExperienceFeedbackRating[];
  overallRating: FeedbackOverallRating;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

export const useInternalFeedbackRatings = ({
  startDate,
  endDate,
  contentMetadataIds,
  countries,
  enabled = true,
  experienceOptions,
}: UseInternalFeedbackRatingsParams): UseInternalFeedbackRatingsResult => {
  const { companyId } = useParams();
  const learningManagementService = useLearningManagementServiceV1();

  const normalizedRange = useMemo(
    () => normalizeMetricDateRange(startDate, endDate),
    [startDate, endDate],
  );

  const { startDate: normalizedStartDate, endDate: normalizedEndDate } =
    normalizedRange;

  const { data, error, isLoading, isFetching } = useQuery({
      queryKey: [
        FEEDBACK_RATINGS_QUERY_KEY,
        companyId,
        normalizedStartDate.toISOString(),
        normalizedEndDate.toISOString(),
        contentMetadataIds,
        countries,
      ],
      queryFn: async () => {
        if (!companyId) {
          throw new Error("Company ID is required");
        }

        const filter = toApiLearningMetricFilter({
          startDate: normalizedStartDate,
          endDate: normalizedEndDate,
          contentMetadataIds,
          countries,
        });

        const result = await learningManagementService.getCompanyRatings(
          companyId,
          filter,
        );

        return toExperienceRatingSeries(result);
      },
      enabled: Boolean(companyId) && enabled,
      staleTime: STALE_TIME_MS,
      gcTime: GC_TIME_MS,
      placeholderData: keepPreviousData,
      retry: 1,
    });

  const feedbackData = useMemo<ExperienceFeedbackRating[]>(() => {
    if (!data) return [];

    if (!experienceOptions || experienceOptions.length === 0) {
      return data.experiences.map((rating) => ({
        experienceId: rating.experienceId,
        experienceName: rating.experienceName,
        ...extractRatingValues(rating.values),
      }));
    }

    const dataPointLookup = new Map(
      data.experiences.map((rating) => [rating.experienceId, rating]),
    );

    const optionValues = new Set(experienceOptions.map((o) => o.value));

    const optionRows = experienceOptions.map((option) => ({
      experienceId: option.value,
      experienceName: option.label,
      ...extractRatingValues(dataPointLookup.get(option.value)?.values),
    }));

    const unmatchedRows = data.experiences
      .filter((rating) => !optionValues.has(rating.experienceId))
      .map((rating) => ({
        experienceId: rating.experienceId,
        experienceName: rating.experienceName,
        ...extractRatingValues(rating.values),
      }));

    return [...optionRows, ...unmatchedRows];
  }, [data, experienceOptions]);

  const overallRating = useMemo(
    () => computeOverallRating(data?.aggregateValues),
    [data],
  );

  return {
    data: feedbackData,
    overallRating,
    isLoading,
    isFetching,
    error: error ?? null,
  };
};
